/* eslint-disable no-underscore-dangle, no-case-declarations */
import { useLang } from "@hooks/useLang";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ANALYTICS_DEFAULT_DATE_TIME,
  FilterType,
  getDefaultChartOrder,
  ObjectType
} from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import { CalendarModeRangeType } from "@custom-types/Types";
import _ from "lodash";
import { FlatNodeItem } from "@components/common/tree-view/Interfaces";
import { HVButton, useHVSnackbar } from "@repo/HVComponents";
import moment from "moment";
import { generateTimezoneList } from "@utils/Timezone";
import { useCurrentTimezone } from "@hooks/useCurrentTimezone";
import { GetUserSettings } from "@queries/User";
import {
  CALENDAR_MODE_RANGE_TYPE,
  SESSION_STORAGE_KEY
} from "@resources/Constants";
import useSessionStorage from "@hooks/useSessionStorage";
import { DateTime } from "@components/report/Interfaces";
import { getDateTimeCalendar } from "@utils/Widget";
import {
  FilterListAccordionButton,
  MORE_TYPE
} from "./btns/accordion/FilterListAccordionButton";
import * as S from "./LeftMenu.styled";
import { SaveButton } from "./btns/SaveButton";
import { CalendarButton } from "./btns/CalendarButton";
import { RuleButton } from "./btns/RuleButton";
import { ObjectTypeButton } from "./btns/ObjectTypeButton";

const DEFAULT_FILTER: FilterType = {
  filterName: "FilterName",
  order: [],
  rules: [],
  baseTime: ANALYTICS_DEFAULT_DATE_TIME,
  objectType: "People"
};

interface LeftMenuProps {
  removedFromHeader?: string;
  setRemovedFromHeader?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  savedFilters: FilterType[];
  savedFilterIdx: number;
  searchedFilter?: FilterType;
  onSearch: (filter: FilterType) => void;
  onSave: (
    type: "SAVE" | "SAVE_AS",
    options: { fileName: string; id?: string }
  ) => void;
  onRemove: (id: string) => void;
  onChangeTab: (id: string) => void;
  onChangeFilterName: (id: string, filterName: string) => void;
  settingData?: GetUserSettings;
}

export const analyticsDefaultDateTime = (
  mode: CalendarModeRangeType,
  timeZone: string
): DateTime => {
  return {
    ...getDateTimeCalendar(
      CALENDAR_MODE_RANGE_TYPE.LAST_7_DAYS,
      "",
      undefined,
      moment().subtract(1, "d").tz(timeZone).toISOString(true),
      timeZone
    ),
    mode
  };
};

export const LeftMenu = (props: LeftMenuProps) => {
  // hooks
  const { translate } = useLang();
  const { currentTimeZone } = useCurrentTimezone();
  const [, setSessionStorageUtc] = useSessionStorage(
    SESSION_STORAGE_KEY.TIME_ZONE,
    moment.tz.guess()
  );
  const { showSnackbar } = useHVSnackbar();

  // state
  const [currentAnalyticsTimeZone, setCurrentAnalyticsTimeZone] =
    useState<string>(currentTimeZone);
  const selectedFilter = useMemo(() => {
    const updateDateTime = (filter: FilterType): FilterType => {
      return {
        ...filter,
        baseTime: analyticsDefaultDateTime(
          filter.baseTime?.mode as CalendarModeRangeType,
          currentAnalyticsTimeZone
        )
      };
    };

    if (
      Array.isArray(props.savedFilters) &&
      props.savedFilters[props.savedFilterIdx]
    ) {
      return props.savedFilters[props.savedFilterIdx];
    }

    return _.cloneDeep(updateDateTime(DEFAULT_FILTER));
  }, [props.savedFilters, props.savedFilterIdx]);
  const [tempSavedFilter, setTempSavedFilter] =
    useState<FilterType>(selectedFilter);

  const [currentUserTimeZone, setCurrentUserTimeZone] = useState<string>(
    props.settingData?.dateTime?.timezone ?? currentTimeZone
  );

  const selectedFilterId = props.savedFilters[props.savedFilterIdx]?._id;
  const prevTempSavedFilter = useRef<FilterType>(tempSavedFilter);
  const [isSearched, setIsSearched] = useState(false);
  const utc = useMemo(() => {
    const utcItem = generateTimezoneList().filter((item) => {
      return item?.value === currentAnalyticsTimeZone;
    });
    if (utcItem.length > 0) {
      const label = utcItem[0].label.split(" ");
      return label[0].replace(/[()]/g, "") as string;
    }
    return "";
  }, [currentAnalyticsTimeZone]);
  const filterList = useMemo(() => {
    if (props.savedFilters.length > 0) {
      return props.savedFilters.map((filter) => ({
        value: filter._id ?? "",
        label: filter.filterName
      }));
    }

    return undefined;
  }, [props.savedFilters]);

  // effects

  useEffect(() => {
    if (selectedFilter._id) {
      setTempSavedFilter(selectedFilter);
      prevTempSavedFilter.current = _.cloneDeep(selectedFilter);
    }
  }, [selectedFilter._id]);

  useEffect(() => {
    if (props?.savedFilters?.[props.savedFilterIdx]?.timeZone) {
      // when change prop timezone local state update
      setCurrentAnalyticsTimeZone(
        props.savedFilters[props.savedFilterIdx].timeZone as string
      );

      setSessionStorageUtc(
        props.savedFilters[props.savedFilterIdx].timeZone as string
      );
      setCurrentAnalyticsTimeZone(
        props.savedFilters[props.savedFilterIdx].timeZone as string
      );
    } else if (props?.settingData?.dateTime?.timezone) {
      setSessionStorageUtc(props.settingData.dateTime.timezone);
      setCurrentAnalyticsTimeZone(props.settingData.dateTime.timezone);
    }

    if (props?.settingData?.dateTime) {
      setCurrentUserTimeZone(props.settingData.dateTime.timezone);
    }
  }, [
    props?.savedFilterIdx,
    props?.savedFilters,
    props?.settingData?.dateTime?.timezone
  ]);

  const switchFilterAfterRemove = (deletedId: string) => {
    if (selectedFilter._id !== deletedId) {
      const filter =
        _.cloneDeep(
          props.savedFilters.find((item) => item._id === selectedFilter._id)
        ) ?? DEFAULT_FILTER;
      setTempSavedFilter(filter);
    } else {
      const filtered = props.savedFilters.filter(
        (item) => item._id !== deletedId
      );
      if (filtered.length > 0) {
        setTempSavedFilter(_.cloneDeep(filtered[0]));
      } else {
        setIsSearched(false);
        setTempSavedFilter(_.cloneDeep(DEFAULT_FILTER));
      }
    }
  };

  useEffect(() => {
    if (props.removedFromHeader) {
      switchFilterAfterRemove(props.removedFromHeader);
      props.setRemovedFromHeader?.(undefined);
    }
  }, [props.removedFromHeader, props.setRemovedFromHeader]);

  const handleSearch = () => {
    if (tempSavedFilter) {
      if (_.isEqual(prevTempSavedFilter.current, tempSavedFilter)) {
        return;
      }

      const newTempSavedFilter = {
        ...tempSavedFilter,
        order: getDefaultChartOrder(tempSavedFilter.rules),
        timeZone: currentAnalyticsTimeZone
      };
      prevTempSavedFilter.current = _.cloneDeep(newTempSavedFilter);
      props.onSearch(newTempSavedFilter);
      setTempSavedFilter(newTempSavedFilter);
      setIsSearched(true);
    }
  };

  const handleMore = (moreType: MORE_TYPE, id: string) => {
    switch (moreType) {
      case "REMOVE_FILTER":
        if (id) {
          switchFilterAfterRemove(id);
          props.onRemove(id);
        }
        break;
      default:
        break;
    }
  };

  const disableSearch = (() => {
    const isNotSearched = isSearched && !props.searchedFilter;
    if (isNotSearched) {
      return true;
    }

    const isAllCategorySelected =
      !tempSavedFilter?.baseTime ||
      !tempSavedFilter?.rules ||
      tempSavedFilter?.rules.length === 0 ||
      !tempSavedFilter?.objectType;
    if (isAllCategorySelected) {
      return true;
    }

    const isNotChangedDate =
      !!props.searchedFilter?.baseTime &&
      !!tempSavedFilter?.baseTime &&
      moment(props.searchedFilter.baseTime.start)
        .tz(
          props.searchedFilter.timeZone ||
            (props.settingData?.dateTime?.timezone as string)
        )
        .isSame(moment(tempSavedFilter.baseTime.start), "date") &&
      moment(props.searchedFilter.baseTime.end)
        .tz(
          props.searchedFilter.timeZone ||
            (props.settingData?.dateTime?.timezone as string)
        )
        .isSame(moment(tempSavedFilter.baseTime.end), "date");

    if (!isNotChangedDate) {
      return false;
    }
    const isNotChangedTimeZone =
      currentAnalyticsTimeZone === tempSavedFilter?.timeZone;

    if (!isNotChangedTimeZone) {
      prevTempSavedFilter.current = {
        ...prevTempSavedFilter.current,
        timeZone: currentAnalyticsTimeZone
      };
      return false;
    }
    const isNotChangedOther =
      props.searchedFilter &&
      _.isEqual(
        _.pick(props.searchedFilter, ["rules", "objectType"]),
        _.pick(tempSavedFilter, ["rules", "objectType"])
      );

    if (!isNotChangedOther) {
      return false;
    }

    return true;
  })();

  const disableSave = (() => {
    if (!isSearched) {
      // if not searched, but changed order
      const isChangedOrder =
        !!props.savedFilters &&
        !!props.searchedFilter &&
        !_.isEqual(
          props.savedFilters[props.savedFilterIdx]?.order,
          props.searchedFilter.order
        );

      if (isChangedOrder) {
        return false;
      }
      return true;
    }

    return false;
  })();

  const isValidName = (name: string) => {
    return !_.isEmpty(name.trim());
  };

  const handleSave = (
    status: "SAVE" | "SAVE_AS",
    options: {
      fileName?: string;
    }
  ) => {
    const fileName =
      options.fileName || props.savedFilters[props.savedFilterIdx].filterName;
    if (fileName && isValidName(fileName)) {
      // change filter name ( save as, change filter name )
      props.onSave(status, { fileName, id: selectedFilterId });
      setTempSavedFilter((prev) => ({ ...prev, filterName: fileName }));
      setIsSearched(false);
    } else {
      showSnackbar({
        id: "enter-right-name",
        message: translate("analytics_enter_right_name"),
        variant: "fail"
      });
    }
  };

  const handleChangeName = (name: string, id: string) => {
    if (!id) {
      showSnackbar({
        id: "filter-id-not-found",
        message: translate("analytics_filter_id_not_found"),
        variant: "fail"
      });
      return;
    }

    if (isValidName(name)) {
      props.onChangeFilterName(id, name);

      if (id === selectedFilter._id) {
        setTempSavedFilter((prev) => ({ ...prev, name }));
      }
      return;
    }
    showSnackbar({
      id: "enter-right-name",
      message: translate("analytics_enter_right_name"),
      variant: "fail"
    });
  };

  const handleChangeTab = (id: string) => {
    const tab =
      props.savedFilters.find((item) => item._id === id) ??
      _.cloneDeep(DEFAULT_FILTER);
    setTempSavedFilter(tab);
    props.onChangeTab(id);
  };

  const handleChangeDate = (v: {
    start: string;
    end: string;
    mode: CalendarModeRangeType;
  }) => {
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      baseTime: v
    }));
  };

  const handleChangeRule = (v: FlatNodeItem[]) => {
    const rules = _.map(v, (rule) => rule.value) as string[];
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      rules
    }));
  };

  const handleChangeObjectType = (objectType: ObjectType) => {
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      rules: [],
      objectType
    }));
  };

  return (
    <S.Container>
      <S.Title>{translate("analytics")}</S.Title>
      <S.FilterWrap>
        <S.FilterName key={`${selectedFilter._id}_filterName`}>
          {translate("save_filter")}
          <div style={{ display: "flex", alignItems: "center" }}>
            <SaveButton
              savedCount={props.savedFilters.length}
              disabled={disableSave}
              onSave={handleSave}
            />
          </div>
        </S.FilterName>
        <div>
          <FilterListAccordionButton
            // key={String(props.savedFilterIdx)}
            disableMore={!filterList}
            defaultId={selectedFilterId}
            list={
              filterList ?? [
                {
                  value: "Analytics list",
                  label: "Analytics list"
                }
              ]
            }
            onChangeFilter={handleChangeTab}
            onChangeMoreType={handleMore}
            onChangeFiltereName={handleChangeName}
          />
        </div>
      </S.FilterWrap>
      <S.Divider />
      <S.FilterWrap>
        <S.FilterName> {translate("period")}</S.FilterName>
        <div>
          <CalendarButton
            key={`${selectedFilter._id}_period_${props.savedFilters[
              props.savedFilterIdx
            ]?.baseTime?.start}-${props.savedFilters[props.savedFilterIdx]
              ?.baseTime?.end}-${props.savedFilters[props.savedFilterIdx]
              ?.baseTime?.mode}`}
            dateTime={tempSavedFilter?.baseTime as DateTime}
            onChange={handleChangeDate}
            currentTimeZone={currentAnalyticsTimeZone}
            setCurrentTimeZone={setCurrentAnalyticsTimeZone}
            currentUserTimeZone={currentUserTimeZone}
            utc={utc}
          />
        </div>
      </S.FilterWrap>
      <S.FilterWrap key={`${selectedFilter._id}_objectType`}>
        <S.FilterName>{translate("object_type")}</S.FilterName>
        <ObjectTypeButton
          defaultType={tempSavedFilter.objectType}
          onChange={handleChangeObjectType}
        />
      </S.FilterWrap>
      <S.FilterWrap key={`${selectedFilter._id}_location`}>
        <S.FilterName>{translate("locations")}</S.FilterName>
        <RuleButton
          key={`${tempSavedFilter.objectType}_${props.savedFilterIdx}`}
          ruleCategory={tempSavedFilter.objectType}
          defaultRules={tempSavedFilter.rules ?? []}
          onChange={handleChangeRule}
        />
      </S.FilterWrap>
      <S.BottomWrap key={`${selectedFilter._id}_search`}>
        <HVButton
          onClick={handleSearch}
          buttonContainerProps={{
            disabled: disableSearch,
            w: "100%",
            style: { width: "100%", height: "32px", padding: "0px 16px" }
          }}
        >
          {translate("search")}
        </HVButton>
      </S.BottomWrap>
    </S.Container>
  );
};
